import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/gutter.js';



export default function GutterPage() {
  const service = {
    title: 'Gutter Installation & Maintenance',
    image: '/hero/best-rain-gutter-services-in-los-angeles.webp',
    description:
      'Gutter installation & maintenance by Modern Build. Protect your home from water damage. Free estimate: (323) 300-4130',
    link: '/services/gutter',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Gutter Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Gutter Installation in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="Gutter Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Gutter installation & maintenance by Modern Build. Protect your home from water damage. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="gutter installation Los Angeles, seamless gutters, gutter repair, gutter cleaning, rain gutters LA"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/gutter" />

        <meta property="og:title" content="Gutter Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Gutter installation & maintenance by Modern Build. Protect your home from water damage. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-rain-gutter-services-in-los-angeles.webp"
        />
        <meta property="og:url" content="https://modernbuildremodel.com/services/gutter" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gutter Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Gutter installation & maintenance by Modern Build. Protect your home from water damage. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-rain-gutter-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Gutter Installation in Los Angeles | Free Estimate | Modern Build',
                description:
                  'Reliable gutter services in Los Angeles. We install seamless systems that protect your home from water intrusion and foundation damage.',
                slug: '/services/gutter',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="gutter" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="SEAMLESS GUTTER EXPERTS"
        title="Gutter Installation in Los Angeles"
        background="white"
        description="We design and install seamless gutters that efficiently manage rainwater and protect your home’s siding, foundation, and landscaping."
        extraText="Our gutters are made from rust-resistant aluminum or copper and installed with precision for long-term durability."
        phone="(323) 300-4130"
        image1="/split/gutter-installation-los-angeles.webp"
        image2="/split/rain-gutters-los-angeles.webp"
      />

      <Benefits
        title="Why Choose Modern Build for Gutter Services?"
        subtitle="Custom-fit gutters, reliable protection"
        items={[
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Seamless Aluminum & Copper Gutters",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "Custom Cut to Fit Your Roofline",
          },
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Leak-Free Gutter Installation",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Debris Guards & Leaf Protection",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Gutter Repairs & Replacements",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Free Estimates & Fast Scheduling",
            href: "/contact",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Protects Foundation & Landscaping",
            href: "/services/outdoor-living",
          },
          {
            icon: <CalendarClock className="w-8 h-8 text-primary" />,
            title: "Quick Turnaround, Lasting Results",
          },
        ]}
      />


      <ContentBlock
        subtitle="STOP COSTLY WATER DAMAGE"
        title="Protect Your Property with Quality Gutters"
        description="Poor drainage can lead to mold, erosion, and structural issues. Our gutter systems direct water away from your home — reducing risk and preserving property value."
        extraText="Whether you need a full installation or routine cleaning, our team delivers long-lasting gutter solutions."
        buttonText="Get a Free Gutter Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Gutter Installation Process"
        subtitle="Efficient and streamlined from start to finish"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Gutter Needs Assessment",
            description: "We visit your home and evaluate existing drainage, fascia, and roof slope to recommend the best solution."
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Custom Fabrication",
            description: "Gutters are measured and cut on-site for a perfect fit. We select materials and guards based on your needs."
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Professional Installation",
            description: "We secure gutters with hidden hangers, test drainage, and apply sealants where necessary."
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Cleanup & Final Walkthrough",
            description: "After installation, we clean up thoroughly and walk you through maintenance tips and warranty details."
          },
        ]}
      />

      <Split
        subtitle="DEFEND YOUR HOME FROM RAIN DAMAGE"
        title="Seamless Gutters for Los Angeles Homes"
        background="white"
        description="Gutters are one of the most important defenses against water intrusion. Our installations provide lasting performance and enhanced curb appeal."
        extraText="Our team has experience with all roof types and offers gutter solutions tailored for your home's structure and climate."
        phone="(323) 300-4130"
        image1="/split/seamless-gutter-los-angeles.webp"
        image2="/split/gutter-cleaning-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Book Your Gutter Service Today"
        subtitle="Custom gutters installed for maximum protection"
        buttonText="Schedule Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="gutter" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}